import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { lensesData, getAllLenses } from '../data/lensesData';
import { productGuidesData } from '../data/productGuidesData';

function LensDetail() {
  const { lensId } = useParams();
  const lens = lensesData[lensId];
  const allLenses = getAllLenses();

  if (!lens) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-4xl text-charcoal mb-4">Lens Not Found</h1>
          <p className="text-gray-500 mb-8">The lens you're looking for doesn't exist in our library.</p>
          <Link to="/lenses" className="text-sage-600 hover:text-sage-700 font-medium">
            ← Back to Lenses Library
          </Link>
        </div>
      </div>
    );
  }

  // Get related lenses (same tier, excluding current)
  const relatedLenses = allLenses
    .filter(l => l.id !== lens.id)
    .slice(0, 3);

  // Get product guides for this lens
  const relatedGuides = productGuidesData.filter(guide => guide.lensId === lens.id);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{lens.name} - Research & Health Effects | FoodLens AI</title>
        <meta name="description" content={`${lens.tagline}. ${lens.description.substring(0, 150)}... Learn about health concerns and scientific research.`} />
        <meta name="keywords" content={`${lens.name.toLowerCase()}, ${lens.ingredients.join(', ').toLowerCase()}, food ingredients, health effects, nutrition research`} />
        <meta name="author" content="FoodLens AI Research Team" />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${lens.name} - Research & Health Effects`} />
        <meta property="og:description" content={`${lens.tagline}. Learn about ${lens.name.toLowerCase()} in food and the research behind it.`} />
        <meta property="og:url" content={`https://foodlens-ai.com/lenses/${lens.id}`} />
        <meta property="og:site_name" content="FoodLens AI" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${lens.name} - Research & Health Effects`} />
        <meta name="twitter:description" content={lens.tagline} />

        {/* Canonical URL */}
        <link rel="canonical" href={`https://foodlens-ai.com/lenses/${lens.id}`} />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Breadcrumb Navigation */}
        <nav className="mb-8 text-sm">
          <ol className="flex items-center space-x-2 text-gray-500">
            <li>
              <Link to="/" className="hover:text-sage-600 transition-colors">Home</Link>
            </li>
            <li>
              <span className="mx-2">/</span>
            </li>
            <li>
              <Link to="/lenses" className="hover:text-sage-600 transition-colors">Lenses</Link>
            </li>
            <li>
              <span className="mx-2">/</span>
            </li>
            <li className="text-charcoal font-medium">{lens.name}</li>
          </ol>
        </nav>

        {/* Header */}
        <header className="mb-10">
          <div className="flex items-center mb-4">
            <span className="text-5xl mr-4">{lens.icon}</span>
            <div>
              <div className="mb-2 flex items-center gap-2">
                <span className={`inline-block px-3 py-1 rounded-lg text-sm font-medium ${
                  lens.tier === 'free'
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-purple-100 text-purple-700'
                }`}>
                  {lens.tier === 'free' ? 'Free Lens' : 'Premium Lens'}
                </span>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl text-charcoal">
                {lens.name}
              </h1>
            </div>
          </div>
          <p className="text-xl text-gray-600 mt-4 font-medium" style={{ color: lens.color }}>
            {lens.tagline}
          </p>
        </header>

        {/* Overview Section */}
        <section className="mb-12 p-6 bg-sage-50 rounded-2xl border border-sage-100">
          <h2 className="font-display text-2xl text-charcoal mb-4">Overview</h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            {lens.description}
          </p>
        </section>

        {/* Key Facts Section */}
        <section className="mb-12">
          <h2 className="font-display text-2xl text-charcoal mb-6">Key Facts</h2>
          <div className="space-y-4">
            {lens.keyFacts.map((fact, index) => (
              <div key={index} className="flex items-start p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-xl">
                <svg className="w-6 h-6 text-amber-600 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <p className="text-gray-700">{fact}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Ingredients Detected Section */}
        <section className="mb-12">
          <h2 className="font-display text-2xl text-charcoal mb-6">Ingredients Detected</h2>
          <p className="text-gray-500 mb-4">
            This lens detects the following ingredients and their variations:
          </p>
          <div className="flex flex-wrap gap-2">
            {lens.ingredients.map((ingredient, index) => (
              <span
                key={index}
                className="inline-block px-4 py-2 rounded-xl text-sm font-medium"
                style={{
                  backgroundColor: `${lens.color}15`,
                  color: lens.color
                }}
              >
                {ingredient}
              </span>
            ))}
          </div>
          <p className="text-sm text-gray-400 mt-4">
            FoodLens also detects alternative names and variations of these ingredients.
          </p>
        </section>

        {/* Product Guides Section */}
        {relatedGuides.length > 0 && (
          <section className="mb-12">
            <h2 className="font-display text-2xl text-charcoal mb-6">
              {lens.name}-Free Product Guides
            </h2>
            <p className="text-gray-500 mb-6">
              Find products without {lens.name.toLowerCase()} in these categories:
            </p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {relatedGuides.map((guide) => (
                <Link
                  key={guide.slug}
                  to={`/${guide.slug}`}
                  className="p-4 bg-surface border border-sage-100 rounded-xl hover:shadow-soft hover:-translate-y-1 transition-all group"
                >
                  <h3 className="font-semibold text-charcoal mb-1 group-hover:text-sage-600 transition-colors">
                    {guide.categoryLabel}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {guide.products.length} recommended products
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Scientific Research Section */}
        <section className="mb-12">
          <h2 className="font-display text-2xl text-charcoal mb-6">Scientific Research</h2>
          <p className="text-gray-500 mb-6">
            The following peer-reviewed studies and research papers provide scientific evidence about {lens.name.toLowerCase()}:
          </p>
          <div className="space-y-6">
            {lens.references.map((ref, index) => (
              <article key={index} className="p-6 bg-surface border border-sage-100 rounded-2xl hover:shadow-soft transition-shadow">
                <h3 className="font-semibold text-charcoal mb-3">
                  {ref.title}
                </h3>

                <div className="mb-3 text-sm text-gray-500">
                  <p className="mb-1">
                    <span className="font-medium">Authors:</span> {ref.authors}
                  </p>
                  <p className="mb-1">
                    <span className="font-medium">Published in:</span> {ref.publication} ({ref.year})
                  </p>
                  {ref.doi && (
                    <p className="mb-1">
                      <span className="font-medium">DOI:</span> {ref.doi}
                    </p>
                  )}
                </div>

                <a
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sage-600 hover:text-sage-700 font-medium transition-colors"
                >
                  View Full Study
                  <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                  </svg>
                </a>
              </article>
            ))}
          </div>
        </section>

        {/* How FoodLens Helps Section */}
        <section className="mb-12 p-8 bg-gradient-sage rounded-2xl text-white">
          <h2 className="font-display text-2xl mb-4">
            Detect {lens.name} with FoodLens AI
          </h2>
          <p className="text-lg text-white/90 mb-6">
            FoodLens AI instantly identifies {lens.name.toLowerCase()} in food labels. Simply scan any ingredient label to see if these concerning ingredients are present.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/app"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-sage-600 font-medium rounded-xl hover:bg-sage-50 transition-colors"
            >
              Download FoodLens AI
            </Link>
            <Link
              to="/extension"
              className="inline-flex items-center justify-center px-6 py-3 bg-white/10 text-white font-medium rounded-xl border border-white/30 hover:bg-white/20 transition-colors"
            >
              Get Browser Extension
            </Link>
          </div>
        </section>

        {/* Related Lenses Section */}
        <section className="mb-12">
          <h2 className="font-display text-2xl text-charcoal mb-6">Explore Other Lenses</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {relatedLenses.map((relatedLens) => (
              <Link
                key={relatedLens.id}
                to={`/lenses/${relatedLens.id}`}
                className="p-6 bg-surface border border-sage-100 rounded-2xl hover:shadow-soft hover:-translate-y-1 transition-all"
              >
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-2">{relatedLens.icon}</span>
                  <h3 className="font-semibold text-charcoal">{relatedLens.name}</h3>
                </div>
                <p className="text-sm text-gray-500 line-clamp-2">{relatedLens.tagline}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* Browse All Link */}
        <div className="text-center">
          <Link
            to="/lenses"
            className="inline-flex items-center text-sage-600 hover:text-sage-700 font-medium"
          >
            <svg className="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
            </svg>
            Browse All Lenses
          </Link>
        </div>

        {/* Disclaimer */}
        <div className="mt-12 p-6 bg-sage-50 border-l-4 border-sage-300 rounded-r-xl text-sm text-gray-600">
          <p className="font-semibold mb-2 text-charcoal">Disclaimer</p>
          <p>
            This information is provided for educational purposes only and is not intended as medical advice.
            The research citations and health concerns listed are based on available scientific literature.
            Always consult with healthcare professionals for personalized dietary advice.
          </p>
        </div>
      </div>
    </div>
  );
}

export default LensDetail;
